import { api } from '../../lib/api.ts';

export type Sex = 'MALE' | 'FEMALE';
export type PersonStatus = 'ACTIVE' | 'MOVED_OUT' | 'DECEASED';

/** A person attached directly to a house. */
export interface Person {
  id: string;
  fullName: string;
  nationalId: string | null;
  dateOfBirth: string | null;
  sex: Sex | null;
  relationToHead: string | null;
  maritalStatus: string | null;
  contact: string | null;
  educationLevel: string | null;
  occupation: string | null;
  health: string | null;
  notes: string | null;
  status: PersonStatus;
  movedLocation: string | null;
  movedDate: string | null;
  deceasedDate: string | null;
  returnedDate: string | null;
  createdAt: string;
  updatedAt: string;
  house: { id: string; name: string };
  customData?: Record<string, unknown> | null;
  isHouseOwner: boolean;
}

/** Compact person row as returned in the house detail payload. */
export interface PersonSummary {
  id: string;
  fullName: string;
  status: PersonStatus;
  sex: Sex | null;
  relationToHead: string | null;
}

/** A business (formal or home) attached to a house. */
export interface Business {
  id: string;
  name: string;
  type: string | null;
  active: boolean;
  ownerName?: string | null;
  customData?: Record<string, unknown> | null;
}

/** House detail payload: active persons + both kinds of business. */
export interface HouseDetail {
  id: string;
  name: string;
  number: number | null;
  ward: string | null;
  address: string | null;
  /** The resident marked as this house's owner (at most one), or null. */
  ownerPersonId: string | null;
  persons: PersonSummary[];
  /** Home / household businesses run from the house. */
  businesses: Business[];
  /** Formal registered businesses at the house. */
  houseBusinesses: Business[];
}

/** Body for adding a person. Only fullName is required (quick add). */
export interface PersonInput {
  fullName: string;
  nationalId?: string;
  dateOfBirth?: string;
  sex?: Sex;
  relationToHead?: string;
  maritalStatus?: string;
  contact?: string;
  educationLevel?: string;
  occupation?: string;
  health?: string;
  notes?: string;
  customData?: Record<string, unknown>;
}

/** Editable subset of a person (no status / houseId — those have dedicated calls). */
export type PersonPatch = Partial<{
  fullName: string;
  nationalId: string | null;
  dateOfBirth: string | null;
  sex: Sex | null;
  relationToHead: string | null;
  maritalStatus: string | null;
  contact: string | null;
  educationLevel: string | null;
  occupation: string | null;
  health: string | null;
  notes: string | null;
  customData: Record<string, unknown>;
}>;

export interface FormalBusinessInput {
  name: string;
  type?: string;
  ownerName?: string;
  customData?: Record<string, unknown>;
}

export interface HomeBusinessInput {
  name: string;
  type?: string;
  customData?: Record<string, unknown>;
}

/** Editable subset of a business. */
export type BusinessPatch = Partial<{
  name: string;
  type: string | null;
  ownerName: string | null;
  customData: Record<string, unknown>;
}>;

/** A single lifecycle entry on a resident's status-history timeline. */
export interface PersonHistoryEntry {
  action: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  actor: string | null;
}

export const registerApi = {
  house: (houseId: string) =>
    api.get<{ house: HouseDetail }>(`/register/houses/${houseId}`).then((r) => r.data.house),

  addPerson: (houseId: string, body: PersonInput) =>
    api.post<{ person: Person }>(`/register/houses/${houseId}/persons`, body).then((r) => r.data.person),

  person: (id: string) =>
    api.get<{ person: Person }>(`/register/persons/${id}`).then((r) => r.data.person),

  updatePerson: (id: string, patch: PersonPatch) =>
    api.patch<{ person: Person }>(`/register/persons/${id}`, patch).then((r) => r.data.person),

  personHistory: (id: string) =>
    api
      .get<{ history: PersonHistoryEntry[] }>(`/register/persons/${id}/history`)
      .then((r) => r.data.history),

  setStatus: (
    id: string,
    status: PersonStatus,
    details?: { movedLocation?: string; movedDate?: string; deceasedDate?: string; returnedDate?: string },
  ) =>
    api.post<{ person: Person }>(`/register/persons/${id}/status`, { status, ...details }).then((r) => r.data.person),

  movePerson: (id: string, houseId: string) =>
    api.post<{ person: Person }>(`/register/persons/${id}/move`, { houseId }).then((r) => r.data.person),

  setOwner: (id: string, owner: boolean) =>
    api.post<{ person: Person }>(`/register/persons/${id}/owner`, { owner }).then((r) => r.data.person),

  addBusiness: (houseId: string, body: FormalBusinessInput) =>
    api.post<{ business: Business }>(`/register/houses/${houseId}/businesses`, body).then((r) => r.data.business),

  business: (id: string) =>
    api.get<{ business: Business }>(`/register/businesses/${id}`).then((r) => r.data.business),

  editBusiness: (id: string, patch: BusinessPatch) =>
    api.patch<{ business: Business }>(`/register/businesses/${id}`, patch).then((r) => r.data.business),

  closeBusiness: (id: string) =>
    api.post<{ business: Business }>(`/register/businesses/${id}/close`, {}).then((r) => r.data.business),

  addHomeBusiness: (houseId: string, body: HomeBusinessInput) =>
    api.post<{ business: Business }>(`/register/houses/${houseId}/home-businesses`, body).then((r) => r.data.business),

  homeBusiness: (id: string) =>
    api.get<{ business: Business }>(`/register/home-businesses/${id}`).then((r) => r.data.business),

  editHomeBusiness: (id: string, patch: BusinessPatch) =>
    api.patch<{ business: Business }>(`/register/home-businesses/${id}`, patch).then((r) => r.data.business),

  closeHomeBusiness: (id: string) =>
    api.post<{ business: Business }>(`/register/home-businesses/${id}/close`, {}).then((r) => r.data.business),
};
